import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';

const API = process.env.REACT_APP_API_URL || 'https://freshhillsmilk.in/api';

const fmt     = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

function adminHeaders() {
  const t = localStorage.getItem('adminToken') || localStorage.getItem('token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const Spin = ({ size = 20, color = '#059669' }) => (
  <span style={{
    display: 'inline-block', width: size, height: size,
    border: `2.5px solid ${color}22`, borderTopColor: color,
    borderRadius: '50%', animation: 'ac-spin .7s linear infinite', flexShrink: 0,
  }} />
);

const TIER = (spent) => {
  if (spent >= 5000) return { label: '🥇 Gold',   bg: '#fef3c7', color: '#92400e' };
  if (spent >= 1000) return { label: '🥈 Silver', bg: '#f3f4f6', color: '#374151' };
  return                    { label: '🥉 New',    bg: '#ecfdf5', color: '#065f46' };
};

export default function AdminCustomers() {
  const [stats,     setStats]     = useState(null);
  const [customers, setCustomers] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [pages,     setPages]     = useState(1);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/customers/stats`, { headers: adminHeaders() });
      const d = await r.json();
      if (d.success) setStats(d.stats);
    } catch {}
  }, []);

  const loadCustomers = useCallback(async (p, q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p || page, limit: 20 });
      if (q ?? search) params.set('search', q ?? search);
      const r = await fetch(`${API}/admin/customers?${params}`, { headers: adminHeaders() });
      const d = await r.json();
      if (d.success) { setCustomers(d.customers); setTotal(d.total); setPages(d.pages); }
    } catch {}
    setLoading(false);
  }, [page, search]);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadCustomers(page, search); }, [page]);

  const doSearch = () => { setPage(1); loadCustomers(1, search); };

  const css = `
    @keyframes ac-spin { to { transform:rotate(360deg); } }
    @keyframes ac-fade { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }

    /* Stats */
    .ac-stats{display:grid;grid-template-columns:repeat(2,1fr);gap:.75rem;margin-bottom:1.25rem;}
    @media(min-width:640px){.ac-stats{grid-template-columns:repeat(4,1fr);}}
    .ac-stat{background:white;border-radius:1rem;padding:.9rem 1rem;border:1px solid #e5e7eb;animation:ac-fade .4s;}
    .ac-stat-lbl{font-size:.68rem;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.4px;}
    .ac-stat-val{font-size:1.4rem;font-weight:900;color:#111827;margin-top:.15rem;}
    .ac-stat-val.green{color:#059669;} .ac-stat-val.blue{color:#2563eb;} .ac-stat-val.amber{color:#d97706;}

    /* Legend */
    .ac-legend{display:flex;gap:.6rem;flex-wrap:wrap;margin-bottom:1rem;}
    .ac-leg-item{display:flex;align-items:center;gap:.3rem;font-size:.76rem;font-weight:600;color:#374151;background:white;border:1px solid #e5e7eb;border-radius:9999px;padding:.28rem .72rem;}

    /* Toolbar */
    .ac-toolbar{display:flex;flex-wrap:wrap;gap:.6rem;margin-bottom:1rem;align-items:center;}
    .ac-sw{display:flex;flex:1;min-width:200px;border:1.5px solid #e5e7eb;border-radius:.6rem;overflow:hidden;background:white;}
    .ac-si{flex:1;border:none;outline:none;padding:.55rem .75rem;font-size:.88rem;font-family:'Nunito',sans-serif;}
    .ac-sb{background:#059669;color:white;border:none;padding:.55rem .9rem;cursor:pointer;font-weight:700;font-size:.84rem;font-family:'Nunito',sans-serif;}

    /* Table */
    .ac-card{background:white;border-radius:1rem;border:1px solid #e5e7eb;overflow:hidden;}
    .ac-tw{overflow-x:auto;}
    table.ac-tbl{width:100%;border-collapse:collapse;font-size:.84rem;}
    .ac-tbl th{background:#f9fafb;padding:.7rem 1rem;text-align:left;font-size:.7rem;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:.4px;border-bottom:1px solid #e5e7eb;white-space:nowrap;}
    .ac-tbl td{padding:.72rem 1rem;border-bottom:1px solid #f3f4f6;vertical-align:middle;}
    .ac-tbl tr:last-child td{border-bottom:none;}
    .ac-tbl tr:hover td{background:#fafafa;}
    .ac-badge{display:inline-block;font-size:.7rem;font-weight:700;padding:.2rem .62rem;border-radius:9999px;white-space:nowrap;}
    .ac-avt{width:2rem;height:2rem;border-radius:50%;background:linear-gradient(135deg,#059669,#0891b2);color:white;font-weight:900;font-size:.85rem;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
    .ac-vbtn{background:#f0fdf4;color:#059669;border:1.5px solid #a7f3d0;border-radius:.5rem;padding:.28rem .65rem;font-size:.74rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;}
    .ac-vbtn:hover{background:#dcfce7;}

    /* Pagination */
    .ac-pag{display:flex;align-items:center;justify-content:space-between;padding:.85rem 1rem;border-top:1px solid #f3f4f6;flex-wrap:wrap;gap:.5rem;}
    .ac-pag-info{font-size:.8rem;color:#6b7280;}
    .ac-pag-btns{display:flex;gap:.3rem;flex-wrap:wrap;}
    .ac-pb{border:1.5px solid #e5e7eb;background:white;border-radius:.5rem;padding:.28rem .65rem;font-size:.8rem;font-weight:700;cursor:pointer;font-family:'Nunito',sans-serif;}
    .ac-pb.act{background:#059669;color:white;border-color:#059669;}
    .ac-pb:disabled{opacity:.4;cursor:not-allowed;}

    .ac-ld{display:flex;align-items:center;justify-content:center;padding:3rem;gap:.75rem;color:#6b7280;font-size:.88rem;}

    /* Modal */
    .ac-ov{position:fixed;inset:0;background:rgba(0,0,0,.5);backdrop-filter:blur(3px);z-index:500;display:flex;align-items:center;justify-content:center;padding:1rem;}
    .ac-modal{background:white;border-radius:1.25rem;width:100%;max-width:460px;max-height:90vh;overflow-y:auto;box-shadow:0 24px 60px rgba(0,0,0,.22);animation:ac-fade .3s;}
    .ac-mhdr{background:linear-gradient(135deg,#059669,#0891b2);color:white;padding:1.5rem;border-radius:1.25rem 1.25rem 0 0;text-align:center;position:relative;}
    .ac-mcl{position:absolute;top:.9rem;right:.9rem;background:rgba(255,255,255,.25);border:none;color:white;border-radius:50%;width:1.8rem;height:1.8rem;cursor:pointer;font-size:1rem;display:flex;align-items:center;justify-content:center;}
    .ac-mavt{width:4rem;height:4rem;border-radius:50%;background:rgba(255,255,255,.25);color:white;font-weight:900;font-size:1.6rem;display:inline-flex;align-items:center;justify-content:center;margin-bottom:.5rem;}
    .ac-mnm{font-size:1.1rem;font-weight:800;}
    .ac-mph{font-size:.82rem;opacity:.88;margin-top:.15rem;}
    .ac-mbody{padding:1.25rem 1.5rem;}
    .ac-mg{display:grid;grid-template-columns:1fr 1fr;gap:.65rem;margin-bottom:1rem;}
    .ac-metric{background:#f9fafb;border-radius:.75rem;padding:.8rem;text-align:center;border:1px solid #f3f4f6;}
    .ac-metric-val{font-size:1.2rem;font-weight:900;color:#059669;}
    .ac-metric-lbl{font-size:.68rem;color:#9ca3af;font-weight:600;margin-top:.12rem;}
    .ac-dr{display:flex;justify-content:space-between;padding:.46rem 0;border-bottom:1px solid #f3f4f6;font-size:.86rem;}
    .ac-dr:last-child{border:none;}
    .ac-dl{color:#6b7280;font-weight:600;}
    .ac-dv{font-weight:700;color:#111827;text-align:right;}
  `;

  return (
    <AdminLayout>
      <style>{css}</style>

      {/* Page title */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#111827', marginBottom: '.2rem' }}>
          👥 Customer Analytics
        </div>
        <div style={{ fontSize: '.85rem', color: '#6b7280' }}>
          All registered customers with order history and spending data
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="ac-stats">
          {[
            { lbl: 'Total Customers', val: stats.total_customers,    cls: '' },
            { lbl: 'New Today',       val: stats.new_today,          cls: 'blue' },
            { lbl: 'Active Buyers',   val: stats.active_customers,   cls: 'green' },
            { lbl: 'Avg Spend',       val: fmt(stats.avg_spend),     cls: 'amber' },
          ].map(s => (
            <div key={s.lbl} className="ac-stat">
              <div className="ac-stat-lbl">{s.lbl}</div>
              <div className={`ac-stat-val ${s.cls}`}>{s.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tier legend */}
      <div className="ac-legend">
        <div className="ac-leg-item">🥇 Gold — ₹5000+ spend</div>
        <div className="ac-leg-item">🥈 Silver — ₹1000–4999 spend</div>
        <div className="ac-leg-item">🥉 New — Below ₹1000</div>
      </div>

      {/* Toolbar */}
      <div className="ac-toolbar">
        <div className="ac-sw">
          <input className="ac-si" placeholder="Search by name, phone or email…"
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()} />
          <button className="ac-sb" onClick={doSearch}>🔍 Search</button>
        </div>
        <span style={{ fontSize: '.82rem', color: '#6b7280', fontWeight: 600 }}>
          {total} customer{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="ac-card">
        {loading ? (
          <div className="ac-ld"><Spin /> Loading customers…</div>
        ) : customers.length === 0 ? (
          <div className="ac-ld">👤 No customers found</div>
        ) : (
          <div className="ac-tw">
            <table className="ac-tbl">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Orders</th>
                  <th>Total Spent</th>
                  <th>Last Order</th>
                  <th>Tier</th>
                  <th>Joined</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => {
                  const tier = TIER(c.total_spent);
                  return (
                    <tr key={c.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.55rem' }}>
                          <div className="ac-avt">
                            {(c.name || c.phone || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 700 }}>
                            {c.name || <em style={{ color: '#9ca3af', fontWeight: 400 }}>No name</em>}
                          </span>
                        </div>
                      </td>
                      <td style={{ color: '#374151', fontWeight: 600, fontSize: '.82rem' }}>
                        {c.phone ? `+${c.phone}` : '—'}
                      </td>
                      <td style={{ color: '#6b7280', fontSize: '.8rem' }}>{c.email || '—'}</td>
                      <td style={{ textAlign: 'center', fontWeight: 800, color: c.total_orders > 0 ? '#2563eb' : '#9ca3af' }}>
                        {c.total_orders}
                      </td>
                      <td style={{ fontWeight: 800, color: '#059669' }}>{fmt(c.total_spent)}</td>
                      <td style={{ fontSize: '.76rem', color: '#6b7280' }}>{fmtDate(c.last_order_at)}</td>
                      <td>
                        <span className="ac-badge" style={{ background: tier.bg, color: tier.color }}>
                          {tier.label}
                        </span>
                      </td>
                      <td style={{ fontSize: '.76rem', color: '#9ca3af' }}>{fmtDate(c.created_at)}</td>
                      <td>
                        <button className="ac-vbtn" onClick={() => setSelected(c)}>
                          👁 View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="ac-pag">
            <div className="ac-pag-info">
              Showing {((page - 1) * 20) + 1}–{Math.min(page * 20, total)} of {total} customers
            </div>
            <div className="ac-pag-btns">
              <button className="ac-pb" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
                <button key={p} className={`ac-pb${p === page ? ' act' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="ac-pb" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {/* Customer Detail Modal */}
      {selected && (
        <div className="ac-ov" onClick={() => setSelected(null)}>
          <div className="ac-modal" onClick={e => e.stopPropagation()}>
            <div className="ac-mhdr">
              <button className="ac-mcl" onClick={() => setSelected(null)}>✕</button>
              <div className="ac-mavt">
                {(selected.name || selected.phone || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="ac-mnm">{selected.name || 'No name set'}</div>
              <div className="ac-mph">📱 {selected.phone ? `+${selected.phone}` : '—'}</div>
            </div>
            <div className="ac-mbody">
              {/* Metrics grid */}
              <div className="ac-mg">
                <div className="ac-metric">
                  <div className="ac-metric-val">{selected.total_orders}</div>
                  <div className="ac-metric-lbl">Total Orders</div>
                </div>
                <div className="ac-metric">
                  <div className="ac-metric-val">{fmt(selected.total_spent)}</div>
                  <div className="ac-metric-lbl">Total Spent</div>
                </div>
                <div className="ac-metric">
                  <div className="ac-metric-val">
                    {selected.total_orders > 0
                      ? fmt(selected.total_spent / selected.total_orders)
                      : '₹0.00'}
                  </div>
                  <div className="ac-metric-lbl">Avg Order Value</div>
                </div>
                <div className="ac-metric">
                  <div className="ac-metric-val" style={{ fontSize: '.95rem' }}>
                    {TIER(selected.total_spent).label}
                  </div>
                  <div className="ac-metric-lbl">Customer Tier</div>
                </div>
              </div>

              {/* Detail rows */}
              {[
                ['Customer ID', `#${selected.id}`],
                ['Phone',       selected.phone   ? `+${selected.phone}` : '—'],
                ['Email',       selected.email   || '—'],
                ['Address',     selected.address || '—'],
                ['Last Order',  fmtDate(selected.last_order_at)],
                ['Joined',      fmtDate(selected.created_at)],
              ].map(([l, v]) => (
                <div className="ac-dr" key={l}>
                  <span className="ac-dl">{l}</span>
                  <span className="ac-dv">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}